/**
 * Layout API - POST para /arquivos/exportarPNG
 *
 * Registra exportação de PNGs de layout no backend
 */

import { sendToTerminal } from "../../../lib/terminalLogger";
import { api, APIError } from "../../../lib/api";

export interface LayoutItemPayload {
	objeto_id: string;
	guid?: string | null;
	nome?: string;
	quantidade: number;
	largura?: number | null;
	altura?: number | null;
	nome_arquivo_exportado?: string | null;
	tamanho_arquivo?: number | null;
	md5_arquivo?: string | null;
}

export interface LayoutApiPayload {
	orc_id: number;
	oit_id: number;
	tipo_exportacao: "PNG";
	diretorio_destino: string;
	escala: number;
	transparencia: boolean;
	resolucao_dpi: number;
	objetos: LayoutItemPayload[];
	idempotency_key?: string;
}

/**
 * Constrói payload para POST /arquivos/exportarPNG
 */
export function buildLayoutApiPayload(
	savedData: any,
	layoutResult: any,
	outputDir: string,
	escala: number,
	resolucaoDpi: number,
): LayoutApiPayload | null {
	try {
		const orcId = Number(savedData?.pedido?.orc_id ?? 0) || 0;
		const oitId = Number(savedData?.pedido?.oit_id ?? 0) || 0;

		// Extrair itens: suporta múltiplos formatos de resposta do CorelDRAW
		const itensRaw: any[] = Array.isArray(layoutResult?.result?.exportados)
			? layoutResult.result.exportados
			: Array.isArray(layoutResult?.exportados)
				? layoutResult.exportados
				: Array.isArray(layoutResult?.result?.items)
					? layoutResult.result.items
					: Array.isArray(layoutResult?.items)
						? layoutResult.items
						: [];

		// Mapa de dimensoes dos objetos originais (para validacao de metragem no backend)
		const savedObjetos: any[] = Array.isArray(savedData?.objetos)
			? savedData.objetos
			: [];
		const dimByStaticId: Record<string, { altura?: number; largura?: number }> =
			{};
		for (const so of savedObjetos) {
			const sid = String(so?.staticId ?? so?.id ?? "").trim();
			if (sid)
				dimByStaticId[sid] = {
					altura: so.altura ?? so.height ?? null,
					largura: so.largura ?? so.width ?? null,
				};
		}

		const objetos: LayoutItemPayload[] = itensRaw.map((it, idx) => {
			// objeto_id (era staticId)
			const objetoId = String(it.staticId ?? idx + 1);

			// Nome do arquivo
			const fileName =
				it.nome ||
				it.nome_arquivo_exportado ||
				(it.caminho ? it.caminho.split("\\").pop() : null);

			// Tamanho do arquivo
			const tamanhoArquivo =
				typeof it.tamanho === "number"
					? it.tamanho
					: typeof it.tamanho_arquivo === "number"
						? it.tamanho_arquivo
						: typeof it.size === "number"
							? it.size
							: null;

			// MD5 (do CorelDRAW)
			const md5Arquivo = it.md5 || null;

			// Nome do objeto
			const nome = it.nome || it.name || fileName || objetoId;

			// Quantidade (sempre 1 para PNG de layout)
			const quantidade = typeof it.quantidade === "number" ? it.quantidade : 1;

			// Dimensoes do objeto original (para validacao de metragem no backend)
			const dims = dimByStaticId[objetoId];

			return {
				objeto_id: objetoId,
				guid: it.guid || null,
				nome,
				quantidade,
				largura: dims?.largura ?? null,
				altura: dims?.altura ?? null,
				nome_arquivo_exportado: fileName,
				tamanho_arquivo: tamanhoArquivo,
				md5_arquivo: md5Arquivo,
			};
		});

		const payload: LayoutApiPayload = {
			orc_id: orcId,
			oit_id: oitId,
			tipo_exportacao: "PNG",
			diretorio_destino: outputDir,
			escala,
			transparencia: true,
			resolucao_dpi: resolucaoDpi,
			objetos,
		};

		return payload;
	} catch (e) {
		sendToTerminal("❌ [LAYOUT_API:BUILD_FAILED]", {
			error: e instanceof Error ? e.message : String(e),
		});
		return null;
	}
}

/**
 * Valida payload antes de enviar
 */
function validateLayoutPayload(payload: LayoutApiPayload): {
	valid: boolean;
	error?: string;
} {
	if (!payload || typeof payload !== "object") {
		return { valid: false, error: "Payload ausente" };
	}

	if (!Number.isInteger(payload.orc_id)) {
		return { valid: false, error: "orc_id inválido (deve ser inteiro)" };
	}

	if (!Number.isInteger(payload.oit_id)) {
		return { valid: false, error: "oit_id inválido (deve ser inteiro)" };
	}

	if (payload.tipo_exportacao !== "PNG") {
		return { valid: false, error: "tipo_exportacao deve ser PNG" };
	}

	if (!Array.isArray(payload.objetos) || payload.objetos.length === 0) {
		return { valid: false, error: "objetos deve conter pelo menos 1 item" };
	}

	return { valid: true };
}

/**
 * POST /arquivos/exportarPNG
 */
export async function postLayoutExport(
	payload: LayoutApiPayload,
	idempotencyKey?: string,
): Promise<{
	success: boolean;
	data?: any;
	message?: string;
	status?: number;
}> {
	try {
		// Validação
		const validation = validateLayoutPayload(payload);
		if (!validation.valid) {
			sendToTerminal("❌ [LAYOUT_API:VALIDATION_FAILED]", {
				error: validation.error,
			});
			return { success: false, message: validation.error };
		}

		const idempotency =
			idempotencyKey ||
			((window as any)?.crypto?.randomUUID
				? (window as any).crypto.randomUUID()
				: undefined);

		// Adicionar idempotency ao payload
		const payloadWithKey = {
			...payload,
			idempotency_key: idempotency,
		};

		const data: any = await api("/arquivos/exportarPNG", {
			method: "POST",
			headers: {
				...(idempotency ? { "Idempotency-Key": idempotency } : {}),
			},
			body: JSON.stringify(payloadWithKey),
		});

		const success = Boolean(data?.success ?? data?.ok ?? false);

		return { success, data };
	} catch (e: any) {
		try {
			sendToTerminal("❌ [LAYOUT_API] Erro ao exportar layout", {
				error: e instanceof Error ? e.message : String(e),
				status: e instanceof APIError ? e.status : "unknown",
			});
		} catch {}

		if (e instanceof APIError) {
			const message = e.data?.message || e.message || "Erro na API de layout";
			return { success: false, message, status: e.status, data: e.data };
		}

		return {
			success: false,
			message: e?.message || "Falha na comunicação com API de layout",
		};
	}
}
